import { FC, useMemo } from 'react';

import { ProgressBarMode, Units, ValuePosition } from './helpers';
import {
  BOUNDARIED_MAX_DEFAULT,
  BOUNDARIED_MIN_DEFAULT,
  boundariedValueDefault,
  boundariedValueForShowValue,
  calculateProgress,
} from '@repo-level/core-profile/components/LinearProgressBar/helpers';

import { ScProgressBar, ScProgressBarProps } from './styled';
import { useMedia } from 'modules/common/hooks/useMedia';

export interface ProgressBarProps extends Omit<ScProgressBarProps, 'mode'> {
  from?: number;
  to?: number;
  units?: Units;
  showValue?: ValuePosition;
  mode?: ProgressBarMode;
  withDots?: boolean;
  dotsCount?: number;
  progressMinWidth?: string;
}

export const ProgressBar: FC<ProgressBarProps> = ({
  value,
  units = Units.DEFAULT,
  from = BOUNDARIED_MIN_DEFAULT,
  to = BOUNDARIED_MAX_DEFAULT,
  mode = ProgressBarMode.PRIMARY,
  showValue,
  withDots,
  dotsCount,
  progressMinWidth,
  ...props
}) => {
  const progress = calculateProgress(value, from, to);
  const { isMobile } = useMedia();

  const boundariedValue = showValue
    ? boundariedValueForShowValue(progress)
    : boundariedValueDefault(progress);

  const currentProgress: React.ReactElement = useMemo(
    () => (
      <p className="current-progress">
        {units === Units.DEFAULT ? progress : value}
        {units}
      </p>
    ),
    [value, progress, units]
  );

  return (
    <ScProgressBar
      mode={mode}
      valuePosition={showValue}
      value={boundariedValue}
      withDots={withDots}
      progressMinWidth={progressMinWidth}
      {...props}
    >
      {withDots && dotsCount && (
        <div className="dots">
          {[...Array(dotsCount)].map((_, index) => (
            <span
              key={index}
              className={`dot ${
                (index === 0 && progress === 0) ||
                progress >= index * (100 / dotsCount)
                  ? 'active'
                  : ''
              }`}
            />
          ))}
        </div>
      )}
      <div className="progress-bar-line">
        {showValue === ValuePosition.TOP && currentProgress}
        <div className="completed">
          {showValue === ValuePosition.INSIDE && currentProgress}
        </div>
      </div>
      {(showValue === ValuePosition.RIGHT ||
        (showValue === ValuePosition.BOTTOM && !isMobile)) &&
        currentProgress}
    </ScProgressBar>
  );
};
